import React, { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiOutlineCalendar, HiOutlineLocationMarker, HiOutlineClock, HiOutlineDocumentText, HiCheck } from 'react-icons/hi';
import { datesService } from '../../services/datesService';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import ExpandableText from './ExpandableText';

function DatesManagement({ selectedEdition }) {
  const [datesData, setDatesData] = useState({
    datesHeader: { title: 'Dates Importantes' },
    countdownInfo: { targetDate: '', targetTime: '09:00', isActive: true },
    congressMainInfo: { dateRange: '', location: '', subLocation: '' },
    extensionInfo: { title: '', deadline: '', description1: '', description2: '' },
    ctaInfo: { text: '', publicationNote: '' },
    keyDates: []
  });

  const [editingDate, setEditingDate] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDate, setNewDate] = useState({
    title: '',
    date: '',
    icon: 'HiOutlineDocumentText'
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);

  const iconOptions = [
    { value: 'HiOutlineCalendar', label: 'Calendar', component: <HiOutlineCalendar /> },
    { value: 'HiOutlineLocationMarker', label: 'Location', component: <HiOutlineLocationMarker /> },
    { value: 'HiOutlineClock', label: 'Clock', component: <HiOutlineClock /> },
    { value: 'HiOutlineDocumentText', label: 'Document', component: <HiOutlineDocumentText /> }
  ];

  useEffect(() => {
    if (selectedEdition) {
      loadDatesData();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(datesData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [datesData, originalData]);

  const loadDatesData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      console.log('Loading dates data for edition:', selectedEdition);
      const data = await datesService.getDatesData(selectedEdition);
      
      if (!data || ((!data.datesHeader?.title || data.datesHeader.title === 'Dates Importantes') && 
                    (!data.congressMainInfo?.dateRange || data.congressMainInfo.dateRange === '') &&
                    (!data.congressMainInfo?.location || data.congressMainInfo.location === '') &&
                    (!data.extensionInfo?.title || data.extensionInfo.title === '') &&
                    (!data.keyDates || data.keyDates.length === 0))) {
        console.log('No data found, setting defaults');
        setIsDatabaseEmpty(true);
        const defaultData = {
          datesHeader: { title: 'Dates Importantes' },
          countdownInfo: { 
            targetDate: new Date().toISOString().split('T')[0], 
            targetTime: '09:00', 
            isActive: true 
          },
          congressMainInfo: { 
            dateRange: 'November 20-21, 2025', 
            location: 'Centre de Conférences', 
            subLocation: 'Salle Principale' 
          },
          extensionInfo: { 
            title: 'Extension de la date limite', 
            deadline: 'Date limite: 31 Décembre 2024', 
            description1: 'Description de l\'extension',
            description2: 'Information supplémentaire'
          },
          ctaInfo: { 
            text: 'Ne manquez pas cette opportunité exceptionnelle de participer au congrès.', 
            publicationNote: 'Les articles acceptés seront publiés dans les actes du congrès.'
          },
          keyDates: []
        };
        setDatesData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
      } else {
        console.log('Data found:', data);
        setDatesData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading dates data:', error);
      setIsDatabaseEmpty(true);
      const defaultData = {
        datesHeader: { title: 'Dates Importantes' },
        countdownInfo: { 
          targetDate: new Date().toISOString().split('T')[0], 
          targetTime: '09:00', 
          isActive: true 
        },
        congressMainInfo: { 
          dateRange: 'November 20-21, 2025', 
          location: 'Centre de Conférences', 
          subLocation: 'Salle Principale' 
        },
        extensionInfo: { 
          title: 'Extension de la date limite', 
          deadline: 'Date limite: 31 Décembre 2024', 
          description1: 'Description de l\'extension',
          description2: 'Information supplémentaire'
        },
        ctaInfo: { 
          text: 'Ne manquez pas cette opportunité exceptionnelle de participer au congrès.', 
          publicationNote: 'Les articles acceptés seront publiés dans les actes du congrès.'
        },
        keyDates: []
      };
      setDatesData(defaultData);
      setOriginalData(JSON.parse(JSON.stringify(defaultData)));
    } finally {
      setLoading(false);
    }
  };

  const handleEditDate = (date) => {
    setEditingDate(date);
  };

  const handleDeleteDate = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette date ?')) {
      try {
        await datesService.deleteKeyDate(id);
        setDatesData(prev => ({
          ...prev,
          keyDates: prev.keyDates.filter(date => date.id !== id)
        }));
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting key date:', error);
        alert('Erreur lors de la suppression de la date.');
      }
    }
  };

  const handleSaveDate = async (updatedDate) => {
    try {
      await datesService.updateKeyDate(updatedDate.id, updatedDate);
      setDatesData(prev => ({
        ...prev,
        keyDates: prev.keyDates.map(date =>
          date.id === updatedDate.id ? { ...date, ...updatedDate } : date
        )
      }));
      setEditingDate(null);
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating key date:', error);
      alert('Erreur lors de la modification de la date.');
    }
  };

  const handleAddDate = async () => {
    try {
      if (isDatabaseEmpty) {
        // Add to local state with temporary ID
        const tempDate = {
          ...newDate,
          id: `temp_${Date.now()}`,
          displayOrder: datesData.keyDates.length
        };
        setDatesData(prev => ({
          ...prev,
          keyDates: [...prev.keyDates, tempDate]
        }));
        setHasUnsavedChanges(true);
      } else {
        const newDateFromDb = await datesService.addKeyDate(newDate, selectedEdition);
        const formattedDate = {
          id: newDateFromDb.id,
          title: newDateFromDb.title,
          date: newDateFromDb.date,
          icon: newDateFromDb.icon,
          displayOrder: newDateFromDb.display_order
        };
        
        setDatesData(prev => ({
          ...prev,
          keyDates: [...prev.keyDates, formattedDate]
        }));
        showSuccessMessage();
      }
      
      setNewDate({ title: '', date: '', icon: 'HiOutlineDocumentText' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding key date:', error);
      alert('Erreur lors de l\'ajout de la date.');
    }
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(option => option.value === iconName);
    return iconOption ? iconOption.component : <HiOutlineDocumentText />;
  };

  const handleSave = async () => {
    try {
      await datesService.updateDatesPageData(datesData, selectedEdition);
      
      // Save key dates if any with temp IDs
      for (const keyDate of datesData.keyDates) {
        if (!keyDate.id || keyDate.id.toString().startsWith('temp_')) {
          await datesService.addKeyDate(keyDate, selectedEdition);
        }
      }
      
      setOriginalData(JSON.parse(JSON.stringify(datesData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      showSuccessMessage();
      
      // Reload data to get proper IDs from database
      setTimeout(() => {
        loadDatesData();
      }, 1000);
    } catch (error) {
      console.error('Error saving dates data:', error);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setDatesData(JSON.parse(JSON.stringify(originalData)));
      setHasUnsavedChanges(false);
    }
  };

  const handleUpdateDatesHeader = (title) => {
    setDatesData(prev => ({
      ...prev,
      datesHeader: { title }
    }));
  };

  const handleUpdateCountdownInfo = (field, value) => {
    setDatesData(prev => ({
      ...prev,
      countdownInfo: { 
        ...prev.countdownInfo, 
        [field]: value 
      }
    }));
  };

  const handleUpdateCongressMainInfo = (field, value) => {
    setDatesData(prev => ({
      ...prev,
      congressMainInfo: { 
        ...prev.congressMainInfo, 
        [field]: value 
      }
    }));
  };

  const handleUpdateExtensionInfo = (field, value) => {
    setDatesData(prev => ({
      ...prev,
      extensionInfo: { 
        ...prev.extensionInfo, 
        [field]: value 
      }
    }));
  };

  const handleUpdateCtaInfo = (field, value) => {
    setDatesData(prev => ({
      ...prev,
      ctaInfo: { 
        ...prev.ctaInfo, 
        [field]: value 
      }
    }));
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Dates est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour initialiser les données.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Header Section */}
      <div className="section-header">
        <h3>En-tête des Dates</h3>
      </div>
      <div className="form-group">
        <label>Titre:</label>
        <input 
          type="text" 
          value={datesData.datesHeader.title}
          onChange={(e) => handleUpdateDatesHeader(e.target.value)}
          placeholder="Ex: Dates Importantes"
        />
      </div>

      {/* Countdown Management */}
      <div className="section-header">
        <h3>Gestion du Compte à Rebours</h3>
      </div>
      <div className="form-group">
        <label>Date cible:</label>
        <input 
          type="date" 
          value={datesData.countdownInfo.targetDate}
          onChange={(e) => handleUpdateCountdownInfo('targetDate', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Heure cible:</label>
        <input 
          type="time" 
          value={datesData.countdownInfo.targetTime}
          onChange={(e) => handleUpdateCountdownInfo('targetTime', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>
          <input 
            type="checkbox" 
            checked={datesData.countdownInfo.isActive}
            onChange={(e) => handleUpdateCountdownInfo('isActive', e.target.checked)}
          />
          Activer le compte à rebours
        </label>
      </div>

      {/* Main Congress Info */}
      <div className="section-header">
        <h3>Informations Principales du Congrès</h3>
      </div>
      <div className="form-group">
        <label>Dates du congrès:</label>
        <input 
          type="text" 
          value={datesData.congressMainInfo.dateRange}
          onChange={(e) => handleUpdateCongressMainInfo('dateRange', e.target.value)}
          placeholder="Ex: November 20-21, 2025"
        />
      </div>
      <div className="form-group">
        <label>Lieu principal:</label>
        <input 
          type="text" 
          value={datesData.congressMainInfo.location}
          onChange={(e) => handleUpdateCongressMainInfo('location', e.target.value)}
          placeholder="Ex: Centre de Conférences"
        />
      </div>
      <div className="form-group">
        <label>Sous-lieu:</label>
        <input 
          type="text" 
          value={datesData.congressMainInfo.subLocation}
          onChange={(e) => handleUpdateCongressMainInfo('subLocation', e.target.value)}
          placeholder="Ex: Salle Principale"
        />
      </div>

      {/* Extension Notice */}
      <div className="section-header">
        <h3>Avis d'Extension</h3>
      </div>
      <div className="form-group">
        <label>Titre:</label>
        <input 
          type="text" 
          value={datesData.extensionInfo.title}
          onChange={(e) => handleUpdateExtensionInfo('title', e.target.value)}
          placeholder="Ex: Extension de la date limite"
        />
      </div>
      <div className="form-group">
        <label>Date limite:</label>
        <input 
          type="text" 
          value={datesData.extensionInfo.deadline}
          onChange={(e) => handleUpdateExtensionInfo('deadline', e.target.value)}
          placeholder="Ex: Date limite: 31 Décembre 2024"
        />
      </div>
      <div className="form-group">
        <label>Description 1:</label>
        <textarea 
          value={datesData.extensionInfo.description1}
          onChange={(e) => handleUpdateExtensionInfo('description1', e.target.value)}
          placeholder="Description de l'extension"
        />
      </div>
      <div className="form-group">
        <label>Description 2:</label>
        <textarea 
          value={datesData.extensionInfo.description2}
          onChange={(e) => handleUpdateExtensionInfo('description2', e.target.value)}
          placeholder="Information supplémentaire"
        />
      </div>

      {/* Key Dates Management */}
      <div className="section-header">
        <h3>Gestion des Dates Clés</h3>
        <button className="add-btn" onClick={() => setShowAddForm(true)}>
          <HiPlus /> Ajouter une date
        </button>
      </div>

      {/* Add Date Form */}
      {showAddForm && (
        <div className="add-form">
          <div className="form-group">
            <label>Titre:</label>
            <input 
              type="text" 
              value={newDate.title}
              onChange={(e) => setNewDate({...newDate, title: e.target.value})}
              placeholder="Titre de la date importante"
              required
            />
          </div>
          <div className="form-group">
            <label>Date:</label>
            <input 
              type="text" 
              value={newDate.date}
              onChange={(e) => setNewDate({...newDate, date: e.target.value})}
              placeholder="Ex: 15 Janvier 2025"
              required
            />
          </div>
          <div className="form-group">
            <label>Icône:</label>
            <select 
              value={newDate.icon}
              onChange={(e) => setNewDate({...newDate, icon: e.target.value})}
            >
              {iconOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-actions">
            <button onClick={handleAddDate} className="btn-primary">
              <HiPlus /> Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="btn-secondary">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <DataTable 
          value={datesData.keyDates} 
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          scrollable
          tableStyle={{ tableLayout: 'auto' }}
          emptyMessage="Aucune date clé trouvée."
        >
          <Column 
            field="title" 
            header="Titre" 
            body={(rowData) => (
              editingDate?.id === rowData.id ? (
                <input 
                  type="text" 
                  value={editingDate.title}
                  onChange={(e) => setEditingDate({...editingDate, title: e.target.value})}
                  className="p-inputtext p-component"
                  style={{ width: '100%' }}
                />
              ) : <strong>{rowData.title}</strong>
            )}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="date" 
            header="Date" 
            body={(rowData) => (
              editingDate?.id === rowData.id ? (
                <input 
                  type="text" 
                  value={editingDate.date}
                  onChange={(e) => setEditingDate({...editingDate, date: e.target.value})}
                  className="p-inputtext p-component"
                  style={{ width: '100%' }}
                />
              ) : rowData.date
            )}
            style={{ whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Icône" 
            body={(rowData) => (
              editingDate?.id === rowData.id ? (
                <select 
                  value={editingDate.icon}
                  onChange={(e) => setEditingDate({...editingDate, icon: e.target.value})}
                  className="statut-select"
                >
                  {iconOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex justify-content-center">
                  {getIconComponent(rowData.icon)}
                </div>
              )
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              editingDate?.id === rowData.id ? (
                <div className="flex gap-2">
                  <Button icon="pi pi-check" onClick={() => handleSaveDate(editingDate)} rounded outlined severity="success" size="small" />
                  <Button icon="pi pi-times" onClick={() => setEditingDate(null)} rounded outlined severity="secondary" size="small" />
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button icon="pi pi-pencil" onClick={() => handleEditDate(rowData)} rounded outlined severity="info" size="small" />
                  <Button icon="pi pi-trash" onClick={() => handleDeleteDate(rowData.id)} rounded outlined severity="danger" size="small" />
                </div>
              )
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>

      {/* CTA Section */}
      <div className="section-header">
        <h3>Appel à l'Action</h3>
      </div>
      <div className="form-group">
        <label>Texte principal:</label>
        <textarea 
          value={datesData.ctaInfo.text}
          onChange={(e) => handleUpdateCtaInfo('text', e.target.value)}
          placeholder="Message d'appel à l'action"
        />
      </div>
      <div className="form-group">
        <label>Note de publication:</label>
        <textarea 
          value={datesData.ctaInfo.publicationNote}
          onChange={(e) => handleUpdateCtaInfo('publicationNote', e.target.value)}
          placeholder="Note concernant les publications"
        />
      </div>
    </div>
  );
}


export default DatesManagement;
